import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlTree } from '@angular/router';
import { map, mergeMap, Observable, of, take } from 'rxjs';
import { ComponentName } from '../api/service-providers/models/models/component-name';
import {
  APP_ROUTE_PATHS,
  EDITOR_ROUTE_PATH_BOARD,
  EDITOR_ROUTE_PATH_TENANT,
  EDITOR_ROUTE_PATHS,
  EditorBasePath,
  EditorSubTabPath,
  EditorTabPath,
  NAV_ROUTES,
} from '../app.routes.constant';
import { EMPTY_GUID } from '../constants/empty-guid';
import {
  injectTenantPackagesInclude,
  TenantPackagesInclude,
} from '../functions/inject-functions/inject-tenant-packages-include';
import { isUUID } from '../functions/is-uuid';
import { filterDefined } from '../functions/rxjs-pipes/filter-defined';
import { ClientFeatureFlag, ClientFeatureFlagName } from '../model/client-model/client-feature-flag';
import { BackendFeatureFlagsService } from '../services/backend/backend-feature-flags.service';
import { InterceptorHelperService } from '../services/interceptor-helper.service';
import { isServiceProviderSupportUser, isSuperUserOrDeveloper } from '../services/usercapabilities.service';
import { TenantsFacadeService } from '../store/tenants/tenants.facade.service';
import { UserFacadeService } from '../store/user/user-facade.service';
import { User } from '../store/user/user.types';

interface UserContext {
  user: User;
  featureFlags: ClientFeatureFlag[];
  defaultTenantId: string | undefined;
  defaultBoardId: string | undefined;
}

/**
 * Responsible for guarding the base editor related routes and permitting access or redirecting
 * users based on their authentication status, user role and tenant/board memberships.
 */
export const editorGuard: CanMatchFn = (_, segments) => {
  const featureFlagsService = inject(BackendFeatureFlagsService);
  const interceptorHelperService = inject(InterceptorHelperService);
  const router = inject(Router);
  const userFacadeService = inject(UserFacadeService);
  const pathSegments: string[] = segments.map((s) => s.path);
  const editorBasePath = getEditorBasePath(pathSegments);
  const tenantPackagesInclude = injectTenantPackagesInclude();
  const tenantsFacadeService = inject(TenantsFacadeService);

  return handleRouteAccess(
    editorBasePath,
    pathSegments,
    userFacadeService,
    featureFlagsService,
    router,
    tenantsFacadeService,
    interceptorHelperService,
    tenantPackagesInclude
  );
};

/**
 * Determines the route path type based on path segments
 */
function getEditorBasePath(pathSegments: string[]): EditorBasePath {
  const isEditorPath: boolean =
    pathSegments.length >= 4 &&
    pathSegments[0] === EDITOR_ROUTE_PATH_TENANT &&
    pathSegments[2] === EDITOR_ROUTE_PATH_BOARD;
  const basePath: EditorBasePath = (pathSegments[0] ?? '') as EditorBasePath;

  return isEditorPath ? APP_ROUTE_PATHS.EDITOR.EDITOR.ROOT : basePath;
}

/**
 * Handles route access based on authentication status and route path
 */
function handleRouteAccess(
  editorBasePath: EditorBasePath,
  pathSegments: string[],
  userFacadeService: UserFacadeService,
  featureFlagsService: BackendFeatureFlagsService,
  router: Router,
  tenantsFacadeService: TenantsFacadeService,
  interceptorHelperService: InterceptorHelperService,
  tenantPackagesInclude: TenantPackagesInclude
): Observable<boolean | UrlTree> {
  switch (editorBasePath) {
    case APP_ROUTE_PATHS.EDITOR.ROOT:
    case APP_ROUTE_PATHS.EDITOR.NO_MEMBERSHIP:
    case APP_ROUTE_PATHS.EDITOR.SIGN_IN:
      return handleBasicRouteAccess(userFacadeService, featureFlagsService, router, editorBasePath);

    case APP_ROUTE_PATHS.EDITOR.ADMIN.ROOT:
      return handleAdminRouteAccess(
        userFacadeService,
        featureFlagsService,
        router,
        editorBasePath,
        interceptorHelperService
      );

    case APP_ROUTE_PATHS.EDITOR.EDITOR.ROOT:
      return handleEditorRouteAccess(
        pathSegments,
        userFacadeService,
        featureFlagsService,
        router,
        tenantsFacadeService,
        interceptorHelperService,
        tenantPackagesInclude
      );

    default:
      return of(true);
  }
}

/**
 * Handles access for basic routes
 */
function handleBasicRouteAccess(
  userFacadeService: UserFacadeService,
  featureFlagsService: BackendFeatureFlagsService,
  router: Router,
  editorBasePath: EditorBasePath
): Observable<boolean | UrlTree> {
  return userFacadeService.user$.pipe(
    mergeMap((user) => createUserContext(user, featureFlagsService)),
    map((userContext) => determineRedirect(userContext, editorBasePath, router))
  );
}

/**
 * Handles access for admin routes
 */
function handleAdminRouteAccess(
  userFacadeService: UserFacadeService,
  featureFlagsService: BackendFeatureFlagsService,
  router: Router,
  editorBasePath: EditorBasePath,
  interceptorHelperService: InterceptorHelperService
): Observable<boolean | UrlTree> {
  return userFacadeService.user$.pipe(
    filterDefined,
    mergeMap((user) => {
      if (isSuperUserOrDeveloper(user?.globalRoles) || isServiceProviderSupportUser(user)) {
        const supportUserServiceProviderId = user.tenants.find(
          (t) => t.serviceProviderUserRole === 'SUPPORT'
        )?.serviceProviderId;
        interceptorHelperService.selectedServiceProviderId.set(supportUserServiceProviderId);
        return of(true);
      }
      return createUserContext(user, featureFlagsService).pipe(
        map((userContext) => determineRedirect(userContext, editorBasePath, router))
      );
    })
  );
}

/**
 * Handles access for editor routes
 */
function handleEditorRouteAccess(
  pathSegments: string[],
  userFacadeService: UserFacadeService,
  featureFlagsService: BackendFeatureFlagsService,
  router: Router,
  tenantsFacadeService: TenantsFacadeService,
  interceptorHelperService: InterceptorHelperService,
  tenantPackagesInclude: TenantPackagesInclude
): Observable<boolean | UrlTree> {
  const pathTenantId = pathSegments[1];
  const pathBoardId = pathSegments[3];

  if (!isUUID(pathTenantId) || !isUUID(pathBoardId)) {
    return of(router.parseUrl(''));
  }

  return userFacadeService.user$.pipe(
    mergeMap((user) => {
      let userAccessibleTenantId: string | undefined;
      let userAccessibleBoardId: string | undefined;

      if (isSuperUserOrDeveloper(user.globalRoles) || isServiceProviderSupportUser(user)) {
        userAccessibleTenantId = pathTenantId;
        userAccessibleBoardId = pathBoardId;
      } else {
        userAccessibleTenantId = findUserAccessibleTenantId(user, pathTenantId);
        userAccessibleBoardId = findUserAccessibleBoardId(user, userAccessibleTenantId, pathBoardId);
      }

      if (!userAccessibleTenantId || !userAccessibleBoardId) {
        return of(router.parseUrl(APP_ROUTE_PATHS.EDITOR.NO_MEMBERSHIP));
      }

      interceptorHelperService.selectedTenantId.set(userAccessibleTenantId);
      interceptorHelperService.selectedBoardId.set(userAccessibleBoardId);
      const serviceProviderId = user.tenants.find((t) => t.id === userAccessibleTenantId)?.serviceProviderId;
      if (!!serviceProviderId) {
        interceptorHelperService.selectedServiceProviderId.set(serviceProviderId);
      }

      return tenantsFacadeService
        .getEnrichedTenant$(user.tenants.find((t) => t.id === userAccessibleTenantId) ?? { id: userAccessibleTenantId })
        .pipe(
          mergeMap(() =>
            getCombinedFeatureFlags(featureFlagsService, userAccessibleBoardId).pipe(
              map((featureFlags) => {
                return handleEditorBoardAccess(
                  user,
                  featureFlags,
                  userAccessibleTenantId,
                  userAccessibleBoardId,
                  pathSegments,
                  router,
                  tenantPackagesInclude,
                  tenantsFacadeService
                );
              })
            )
          )
        );
    })
  );
}

/**
 * Handles board access for editor routes
 */
function handleEditorBoardAccess(
  user: User,
  featureFlags: ClientFeatureFlag[],
  userAccessibleTenantId: string,
  userAccessibleBoardId: string,
  pathSegments: string[],
  router: Router,
  tenantPackagesInclude: TenantPackagesInclude,
  tenantsFacadeService: TenantsFacadeService
): boolean | UrlTree {
  const isStaff = isSuperUserOrDeveloper(user.globalRoles) || isServiceProviderSupportUser(user);
  const isTenantOwner = user.tenants?.find((t) => t.id === userAccessibleTenantId)?.role === 'TENANT_OWNER';
  const tabPath = <EditorTabPath | ''>pathSegments[4] ?? '';
  const subTabPath = <EditorSubTabPath>pathSegments[5] ?? '';
  const navigateToRoot = router.parseUrl('');
  const navigateToHome = router.parseUrl(
    NAV_ROUTES.BOARD_EDITOR_HOME_SCREEN(userAccessibleTenantId, userAccessibleBoardId)
  );
  const navigateToInventories = router.parseUrl(
    NAV_ROUTES.BOARD_EDITOR_MULTI_YEAR_INVENTORY(userAccessibleTenantId, userAccessibleBoardId)
  );
  const navigateToHomeIntroduction = router.parseUrl(
    NAV_ROUTES.BOARD_EDITOR_HOME_SCREEN_INTRODUCTION(userAccessibleTenantId, userAccessibleBoardId)
  );

  switch (tabPath) {
    case EDITOR_ROUTE_PATHS.ROOT:
      if (tenantPackagesInclude(ComponentName.Home)) {
        return navigateToHome;
      } else if (tenantPackagesInclude(ComponentName.Inventories)) {
        return navigateToInventories;
      }
      return navigateToRoot;

    case EDITOR_ROUTE_PATHS.HOME.ROOT:
      const isIclei = tenantsFacadeService.serviceProviderName() === 'ICLEI USA';
      const canAccessHomeIntroduction = tenantPackagesInclude(ComponentName.Home) && isIclei;
      if (subTabPath === EDITOR_ROUTE_PATHS.HOME.CHILDREN.INTRODUCTION) {
        return canAccessHomeIntroduction || navigateToHome;
      }
      if (!subTabPath && canAccessHomeIntroduction) {
        return navigateToHomeIntroduction;
      }
      return tenantPackagesInclude(ComponentName.Home) || navigateToInventories;

    case EDITOR_ROUTE_PATHS.INVENTORIES.ROOT:
      if (subTabPath === EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.ECONOMICS) {
        return tenantPackagesInclude(ComponentName.InvestmentPlan) || navigateToInventories;
      }
      if (subTabPath === EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.GOAL) {
        const isV3 = featureFlags.some((f) => f.name === ClientFeatureFlagName.DashboardV3);
        const isV3NewNavigation = featureFlags.some((f) => f.name === ClientFeatureFlagName.NewDashboardNavigationV3);

        return !isV3 || isV3NewNavigation || navigateToInventories;
      }
      return tenantPackagesInclude(ComponentName.Inventories) || navigateToHome;

    case EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT:
      return tenantPackagesInclude(ComponentName.Transitions) || navigateToRoot;

    case EDITOR_ROUTE_PATHS.INTERVENTIONS.ROOT:
      if (subTabPath === EDITOR_ROUTE_PATHS.INTERVENTIONS.CHILDREN.INTERVENTION_LIBRARIES) {
        return (
          (tenantPackagesInclude(ComponentName.Interventions) &&
            tenantPackagesInclude(ComponentName.InterventionLibraries)) ||
          navigateToRoot
        );
      }
      if (subTabPath === EDITOR_ROUTE_PATHS.INTERVENTIONS.CHILDREN.EXPORT_IMPORT) {
        return featureFlags.some((f) => f.name === ClientFeatureFlagName.InterventionsExportImport) || navigateToRoot;
      }
      return tenantPackagesInclude(ComponentName.Interventions) || navigateToRoot;

    case EDITOR_ROUTE_PATHS.INVESTMENT_PLAN:
      return tenantPackagesInclude(ComponentName.InvestmentPlan) || navigateToRoot;

    case EDITOR_ROUTE_PATHS.DASHBOARD.ROOT:
      if (subTabPath === EDITOR_ROUTE_PATHS.DASHBOARD.CHILDREN.EMISSION_STORIES) {
        return (
          (tenantPackagesInclude(ComponentName.DashboardEmissions) &&
            !featureFlags.some(
              (f) => f.name === ClientFeatureFlagName.DashboardV3 || f.name === ClientFeatureFlagName.DashboardV4
            )) ||
          navigateToRoot
        );
      }
      if (subTabPath === EDITOR_ROUTE_PATHS.DASHBOARD.CHILDREN.TRANSITION_STORIES) {
        return tenantPackagesInclude(ComponentName.DashboardEmissions) || navigateToRoot;
      }
      return (
        tenantPackagesInclude(ComponentName.DashboardEmissions) ||
        featureFlags.some((f) => f.name === ClientFeatureFlagName.KeanInventoryAndDashboard) ||
        navigateToRoot
      );

    case EDITOR_ROUTE_PATHS.SETTINGS.ROOT:
      const canAccessSettings = isStaff || isTenantOwner;
      return canAccessSettings || navigateToRoot;

    default:
      return navigateToRoot;
  }
}

/**
 * Finds tenant ID that the user has access to
 */
function findUserAccessibleTenantId(user: User, pathTenantId: string): string | undefined {
  return user.activeState?.activeTenant === pathTenantId
    ? pathTenantId
    : user.tenants?.find((tenant) => tenant.id === pathTenantId)?.id;
}

/**
 * Finds board ID that the user has access to within the tenant
 */
function findUserAccessibleBoardId(
  user: User,
  userAccessibleTenantId: string | undefined,
  pathBoardId: string
): string | undefined {
  if (!userAccessibleTenantId) return undefined;

  return user.activeState?.activeBoard === pathBoardId
    ? pathBoardId
    : user.tenants
        ?.find((tenant) => tenant.id === userAccessibleTenantId)
        ?.boards?.find((board) => board.id === pathBoardId)?.id;
}

/**
 * Creates user context with feature flags
 */
function createUserContext(user: User, featureFlagsService: BackendFeatureFlagsService): Observable<UserContext> {
  const { defaultTenantId, defaultBoardId } = getUserDefaultTenantAndBoardIds(user);
  if (!defaultBoardId) {
    return of({ user, featureFlags: [], defaultTenantId, defaultBoardId });
  }

  return getCombinedFeatureFlags(featureFlagsService, defaultBoardId!).pipe(
    map((featureFlags) => {
      return { user, featureFlags, defaultTenantId, defaultBoardId };
    })
  );
}

/**
 * Gets combined feature flags for a tenant and board
 */
function getCombinedFeatureFlags(
  featureFlagsService: BackendFeatureFlagsService,
  boardId: string
): Observable<ClientFeatureFlag[]> {
  return featureFlagsService.getBoardAndTenantFeatureFlags(boardId).pipe(
    take(1),
    map(({ boardFeatureFlags, tenantFeatureFlags }) => {
      const combinedFeatureFlags = Array.from(
        new Map([...tenantFeatureFlags, ...boardFeatureFlags].map((flag) => [flag.id, flag])).values()
      );
      return combinedFeatureFlags;
    })
  );
}

/**
 * Determines the redirect based on user context
 */
function determineRedirect(
  userContext: UserContext,
  editorBasePath: EditorBasePath,
  router: Router
): boolean | UrlTree {
  const { user, defaultTenantId, defaultBoardId } = userContext;

  if (defaultTenantId && defaultBoardId) {
    return router.parseUrl(NAV_ROUTES.ROOT(defaultTenantId, defaultBoardId));
  }

  if (isSuperUserOrDeveloper(user.globalRoles) || isServiceProviderSupportUser(user)) {
    return router.parseUrl(NAV_ROUTES.ADMIN.ROOT);
  }

  if (editorBasePath === APP_ROUTE_PATHS.EDITOR.NO_MEMBERSHIP) {
    return true;
  }

  return router.parseUrl(NAV_ROUTES.NO_MEMBERSHIP);
}

/**
 * Gets the user's default tenant ID and board ID based on active state or tenant and board membership
 */
function getUserDefaultTenantAndBoardIds(user: User): {
  defaultTenantId: string | undefined;
  defaultBoardId: string | undefined;
} {
  /**
   * 1. Use the tenantId and boardId from the activeState. If the active tenantId or active boardId is an empty GUID, set it to undefined
   */
  let defaultTenantId = user.activeState?.activeTenant;
  let defaultBoardId = user.activeState?.activeBoard;

  if (defaultTenantId === EMPTY_GUID || !user.tenants.some((t) => t.id === defaultTenantId)) {
    defaultTenantId = undefined;
  }

  if (
    defaultBoardId === EMPTY_GUID ||
    !user.tenants.some((t) => (t.boards ?? [])?.some((b) => b.id === defaultBoardId))
  ) {
    defaultBoardId = undefined;
  }

  if (defaultTenantId && defaultBoardId) {
    return { defaultTenantId, defaultBoardId };
  }

  /**
   * 2. If there is an active tenantId but no active boardId, try to find the boardId from the tenant
   */
  if (defaultTenantId && !defaultBoardId) {
    const activeTenant = user.tenants.find((tenant) => tenant.id === defaultTenantId);
    defaultBoardId = activeTenant?.boards?.find((board) => board?.isPrimary)?.id ?? activeTenant?.boards?.[0]?.id;
    if (defaultBoardId) {
      return { defaultTenantId, defaultBoardId };
    }
  }

  /**
   * 3. If there is an active boardId but no active tenantId, try to find the tenantId from the board
   */
  if (defaultBoardId && !defaultTenantId) {
    defaultTenantId = user.tenants?.find((tenant) => tenant.boards?.find((board) => board.id === defaultBoardId))?.id;
    if (defaultTenantId) {
      return { defaultTenantId, defaultBoardId };
    }
  }

  /**
   * 4. If there is no active tenantId or active boardId, try to use the first tenant and board
   */
  if (user.tenants?.length) {
    if (defaultTenantId) {
      const tenantBoards = user.tenants.find((tenant) => tenant.id === defaultTenantId)?.boards;
      defaultBoardId = tenantBoards?.find((board) => board.isPrimary)?.id ?? tenantBoards?.[0]?.id;
    }

    if (!defaultBoardId) {
      for (const tenant of user.tenants) {
        if (tenant.boards?.length) {
          defaultTenantId = tenant.id;
          defaultBoardId = tenant.boards?.find((board) => board.isPrimary)?.id ?? tenant.boards?.[0]?.id;
          break;
        }
      }
    }
  }
  return { defaultTenantId, defaultBoardId };
}
